import { useMemo } from "react";
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Calendar,
  Event as CalendarEvent,
  dayjsLocalizer,
} from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { AbsenceTableCard } from "./Absence.styles";
import { AbsenceEntry } from "../../../types/absence";

const localizer = dayjsLocalizer(dayjs);

type Props = {
  absences: AbsenceEntry[];
  isLoading: boolean;
  getEmployeeName: (employeeId: string) => string;
};

const AbsenceCalendar = ({
  absences,
  isLoading,
  getEmployeeName,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const colorPalette = useMemo(
    () => [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success?.main ?? "#4caf50",
      theme.palette.info?.main ?? "#29b6f6",
      theme.palette.warning?.main ?? "#fb8c00",
      theme.palette.error?.main ?? "#e53935",
      "#7E57C2",
      "#26C6DA",
      "#EC407A",
      "#8D6E63",
    ],
    [theme]
  );

const employeeLegend = useMemo(() => {
  const employeeMap = new Map();

  absences.forEach((absence) => {
    const id = absence.employeeUserId;

    if (id && !employeeMap.has(id)) {
      employeeMap.set(id, {
        id,
        absenceReason: absence.absenceReason,
        label: absence.employeeName,
      });
    }
  });

  return Array.from(employeeMap.values()).map((employeeMap, index) => ({
    ...employeeMap,
    color: colorPalette[index % colorPalette.length],
  }));

}, [absences, colorPalette, getEmployeeName]);

  const events = useMemo<CalendarEvent[]>(() => {
    const approvedAbsences = absences.filter(
      (absence) => absence.absenceApproval === "Approved"
    );

    return approvedAbsences.map((absence) => {
      const startDate = dayjs(absence.startTimestamp).toDate();
      const endDate = dayjs(absence.endTimestamp).add(1, "minute").toDate();

      return {
        title: `${t(
          `serviceModulesPage.absencePage.reason.${absence.absenceReason?.toLocaleLowerCase()}`,
          { defaultValue: absence.absenceReason }
        )} · ${getEmployeeName(absence.employeeUserId)}`,
        allDay: false,
        start: startDate,
        end: endDate,
        resource: absence,
      };
    });
  }, [absences, getEmployeeName, t]);

  const eventPropGetter = (_event: CalendarEvent) => {
    const absence = _event.resource as AbsenceEntry | undefined;
    const employeeId = absence?.employeeUserId ?? "";
    const legendEntry = employeeLegend.find((entry) => entry.id === employeeId);
    const backgroundColor =
      legendEntry?.color ?? theme.palette.primary.main;

    return {
      style: {
        backgroundColor,
        borderColor: backgroundColor,
        color: theme.palette.getContrastText(backgroundColor),
        borderRadius: theme.shape.borderRadius,
        opacity: 0.9,
      },
    };
  };

  return (
    <AbsenceTableCard>
      <Typography variant="h6">
        {t("serviceModulesPage.absencePage.calendar.title")}
      </Typography>

      {employeeLegend.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={2}>
          {employeeLegend.map((entry) => (
            <Stack
              key={entry.id}
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ minWidth: 120 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: entry.color,
                }}
              />
              <Typography variant="body2">
                {entry.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {isLoading && <CircularProgress />}
      {!isLoading && events.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          {t("serviceModulesPage.absencePage.calendar.empty")}
        </Typography>
      )}
      <Box sx={{ height: 500 }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          views={["month", "week", "day"]}
          eventPropGetter={eventPropGetter}
          popup
        />
      </Box>
    </AbsenceTableCard>
  );
};

export default AbsenceCalendar;
